import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
    Box,
    Drawer as MuiDrawer,
    List,
    Typography,
    Divider,
    IconButton,
    Toolbar,
    CssBaseline,
    AppBar as MuiAppBar,
    Grid,
    Popover,
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    ListItem,
    ListItemIcon,
    ListItemText,
    LinearProgress,
} from '@mui/material';
import { People, BadgeOutlined, Person, Menu, ExitToApp, MailOutline, KeyboardArrowDownOutlined } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import MenuAccordion from './common/MenuAccordion';
import Helmet from 'react-helmet';
import { useMediaQuery } from 'react-responsive';
import menuConfig from '../routes/menuConfig';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { signOutUser } from '../redux/actions/userActions';

const drawerWidth = 280;

const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
    }),
}));

const PageContainer = ({ children, tagline = '', btnComponentStyle = {}, buttonComponent, pageTitle = '', prevTitle = '', processing = false, signOutUser, user, showTitle = true }) => {
    const md = useMediaQuery({ query: '(min-width: 1200px)' });
    const sm = useMediaQuery({ query: '(min-width: 750px)' });
    const location = useLocation();
    const [open, setOpen] = useState(true);
    const [logoutDialog, setLogoutDialog] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [userPopover, setUserPopover] = useState(null);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const openPopover = Boolean(anchorEl);
    const id = openPopover ? 'simple-popover' : undefined;

    const toggleMenu = () => {
        setOpen(!open);
    };

    const getActiveLink = (link) => {
        return Boolean(location.pathname.toLowerCase().indexOf(link.toLowerCase()) > -1);
    };

    useEffect(() => {
        setOpen(md);
    }, [md]);

    useEffect(() => {
        setUserPopover([
            {
                name: user?.userData?.lastName + ' ' + user?.userData?.firstName || '',
                icon: <Person />,
                title: 'User',
            },
            {
                name: user?.userData?.employeeId?.padStart(3, '0') || '',
                icon: <BadgeOutlined />,
                title: 'Employee Id',
            },
            {
                name: user?.userData?.username || '',
                icon: <MailOutline />,
                title: 'Email',
            },
            {
                name: user?.userData?.teams?.map(({ name }) => name)?.join(', ') || '',
                icon: <People />,
                title: 'Teams',
            },
        ]);
    }, [user]);

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <Helmet>
                <title>{pageTitle} - CCMS Internal Portal</title>
            </Helmet>

            <AppBar
                position="fixed"
                open={open}
                style={{
                    backgroundColor: 'white',
                    borderBottom: 0,
                    boxShadow: '0 0 2rem 0 rgb(33 37 41 / 10%)',
                }}
            >
                <Toolbar
                    style={{
                        backgroundColor: 'white',
                        boxShadow: 'none',
                        color: 'black',
                    }}
                >
                    <Grid container alignItems="center">
                        <Grid item xs={6}>
                            <IconButton onClick={() => toggleMenu()} className={open ? '' : 'ml-3'}>
                                <Menu />
                            </IconButton>
                        </Grid>
                        <Grid item xs={6} container justifyContent="flex-end" alignItems="center">
                            {sm && (
                                <div>
                                    <Typography fontSize={12} fontWeight={'bold'} textAlign={'right'}>
                                        {user?.userData?.lastName + ' ' + user?.userData?.firstName}
                                    </Typography>
                                    <Typography fontSize={12} textAlign={'right'}>
                                        {user?.userData?.username}
                                    </Typography>
                                </div>
                            )}
                            <IconButton title="Logout" color="inherit" edge="end" className="mr-02" onClick={handleClick}>
                                <KeyboardArrowDownOutlined />
                            </IconButton>

                            <Popover
                                id={id}
                                open={openPopover}
                                anchorEl={anchorEl}
                                onClose={handleClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                            >
                                <List>
                                    {userPopover?.map(({ name, icon, title }, key) => (
                                        <div key={key}>
                                            <ListItem>
                                                <ListItemIcon style={{ minWidth: 35 }}>{icon}</ListItemIcon>
                                                <ListItemText className="no-margin">
                                                    <small style={{ fontSize: 12 }}>{title}</small>
                                                    <Typography padding={0} fontSize={12} fontWeight="bold">
                                                        {name}
                                                    </Typography>
                                                </ListItemText>
                                            </ListItem>
                                            <Divider />
                                        </div>
                                    ))}
                                    <ListItem
                                        onClick={() => {
                                            handleClose();
                                            setLogoutDialog(true);
                                        }}
                                    >
                                        <ListItemIcon style={{ minWidth: 35 }}>
                                            <ExitToApp />
                                        </ListItemIcon>
                                        <ListItemText className="no-margin">
                                            <Typography padding={0} fontSize={12} fontWeight="bold">
                                                Logout
                                            </Typography>
                                        </ListItemText>
                                    </ListItem>
                                </List>
                            </Popover>
                        </Grid>
                    </Grid>
                </Toolbar>
                {processing && <LinearProgress className="m-0" />}
            </AppBar>

            <Drawer
                sx={{
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                    },
                    padding: '.5rem',
                }}
                variant="permanent"
                // anchor="left"
                open={open}
            >
                <Grid mt={open ? 4 : 2} mb={open ? 0 : 2} container flexDirection={'column'} alignItems={'center'} textAlign={'center'} gap={'10px'}>
                    <img src={'/assets/images/logo-sm.png'} width={open ? 40 : 30} height={open ? 50 : 40} />

                    {open && (
                        <h4
                            style={{
                                fontWeight: 'bold',
                                margin: 0,
                                fontSize: '16px',
                                lineHeight: '20px',
                            }}
                        >
                            CCMS
                            <br />
                            <small>Internal Portal</small>
                        </h4>
                    )}
                </Grid>

                <List>
                    {menuConfig.map(({ menus, groupName }, index) => (
                        <>
                            {open && <p className="menu-group">{groupName}</p>}
                            {menus.map((link, key) => (
                                <div key={key} style={{ padding: open ? '0 1rem' : 0 }} className="menu">
                                    <MenuAccordion link={link} open={open} active={getActiveLink(link.path)} />
                                </div>
                            ))}
                        </>
                    ))}
                </List>
            </Drawer>

            <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                <DrawerHeader />
                {showTitle && (
                    <>
                        <Grid container alignItems="center" marginBottom={1} spacing={1} className={'pr-2'}>
                            <Grid item xs={12} lg={6}>
                                <h4 className="title text-bold p-0 m-0 mb-05 f-20">{pageTitle}</h4>
                                {tagline && <Typography fontSize={14}>{tagline}</Typography>}
                                {/* <Typography fontSize={15}>
                            {
                                pageTitle &&
                                <>
                                    <Link to='/'>Home</Link> - {prevTitle?.label && <Link to={prevTitle?.link || '#'}>{prevTitle?.label + ' - '}</Link>} {pageTitle}
                                </>
                            }
                        </Typography> */}
                            </Grid>
                            <Grid item xs={12} lg={6} {...btnComponentStyle} container justifyContent="flex-end">
                                {buttonComponent}
                            </Grid>
                        </Grid>
                        <Divider />
                    </>
                )}
                <Box my={pageTitle && showTitle && 4}>{children}</Box>
            </Box>

            <Dialog open={logoutDialog} onClose={() => setLogoutDialog(false)} maxWidth="md">
                <DialogContent className="pl-2 pr-2 pt-2 pb-1" style={{ width: 400 }}>
                    <Typography variant="h5" fontWeight="bold">
                        Confirm Logout
                    </Typography>
                    <Typography>Are you sure you want to logout?</Typography>
                    <DialogActions className="mt-1">
                        <Button variant="contained" color="secondary" onClick={() => setLogoutDialog(false)}>
                            Cancel
                        </Button>
                        <Button variant="contained" color="primary" onClick={signOutUser}>
                            Confirm
                        </Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

const mapDispatchToProps = (dispatch) => {
    return bindActionCreators({ signOutUser }, dispatch);
};

export default connect(({ user }) => ({ user }), mapDispatchToProps)(PageContainer);
